import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import RootRoute from './RootRoute';
import PublicRoute from './PublicRoute';
import { useAuthStore } from '../store/AuthStore';
import { RoleBasedAccess } from '../components/common/RoleBasedAccess';
import FeatureRoute from '../components/common/FeatureRoute';

const Coordinators = lazy(() => import('../re-entry/coordinators'));
const DataPage = lazy(() => import('../components/dashboard/DataPage'));
const LoginPage = lazy(() => import('../features/auth/pages/LoginPage'));
const CommsPage = lazy(() => import('../components/dashboard/CommsPage'));
const PrivacyPolicy = lazy(() => import('../components/legal/PrivacyPolicy'));
const MarketPlace = lazy(() => import('../components/dashboard/MarketPlace'));
const GovifyPeoplePage = lazy(
  () => import('../components/dashboard/PeoplePage'),
);
const RedactionPage = lazy(
  () => import('../components/redaction/RedactionPage'),
);
const RedactionLandingPage = lazy(
  () => import('../components/dashboard/RedactionLandingPage'),
);
const ResourcesPage = lazy(
  () => import('../components/dashboard/ResourcesPage'),
);
const FacilitiesPage = lazy(
  () => import('../components/dashboard/FacilitiesPage'),
);
const GovifyAppsPage = lazy(
  () => import('../components/dashboard/GovifyAppsPage'),
);
const SetupPasswordPage = lazy(
  () => import('../components/auth/SetupPasswordPage'),
);
const GovifyCalendarPage = lazy(
  () => import('../components/dashboard/CalendarPage'),
);
const TermsAndConditions = lazy(
  () => import('../components/legal/TermsAndConditions'),
);
const GovifyDocumentsPage = lazy(
  () => import('../components/dashboard/DocumentsPage'),
);
const ChatAnalyticsPage = lazy(
  () => import('../components/dashboard/ChatAnalyticsPage'),
);
const ResetPasswordPage = lazy(
  () => import('../features/auth/pages/ResetPasswordPage'),
);
const ForgotPasswordPage = lazy(
  () => import('../features/auth/pages/ForgotPasswordPage'),
);
const GovifySettingsPage = lazy(
  () => import('../components/dashboard/GovifySettingsPage'),
);
const UserManagementPage = lazy(
  () => import('../components/dashboard/UserManagementPage'),
);
const SuperAdminDashboard = lazy(
  () => import('../components/dashboard/SuperAdminDashboard'),
);
const CreateOrganizationPage = lazy(
  () => import('../components/dashboard/CreateOrganizationPage'),
);
const OAuthCallback = lazy(
  () => import('../components/dashboard/CommsPage/components/OAuthCallback'),
);
const GovifySettingsPageVertical = lazy(
  () => import('../components/dashboard/GovifySettingsPageVertical'),
);
const OrganizationManagementPage = lazy(
  () => import('../components/dashboard/OrganizationManagementPage'),
);
const InmateOnboardingFormBuilder = lazy(
  () => import('../components/dashboard/InmateOnboardingFormBuilder'),
);
const InmateOnboardingForm = lazy(
  () =>
    import(
      '../components/dashboard/InmateOnboardingFormBuilder/InmateOnboardingForm'
    ),
);
const OrganizationOnboarding = lazy(
  () => import('../components/dashboard/OrganizationOnboarding'),
);
const OrganizationDetailPage = lazy(
  () => import('../components/dashboard/OrganizationDetailPage'),
);
const OrganizationApplicationsForm = lazy(
  () =>
    import(
      '../components/dashboard/OrganizationOnboarding/OrganizationApplicationsForm'
    ),
);
const ReentryFormsPage = lazy(
  () => import('../components/reentry/builder/ReentryFormsPage'),
);
const FormBuilderPage = lazy(
  () => import('../components/reentry/builder/FormBuilderPage'),
);
const ReentryAnalyticsPage = lazy(
  () => import('../components/reentry/analytics/ReentryAnalyticsPage'),
);
const ReentryDevicesPage = lazy(
  () => import('../components/reentry/devices/ReentryDevicesPage'),
);
const KioskGuard = lazy(() => import('../components/reentry/kiosk/KioskGuard'));
const KioskForm = lazy(() => import('../components/reentry/kiosk/KioskForm'));
const GrantsPage = lazy(() => import('../components/dashboard/GrantsPage'));

// Role-based redirect component for root path
const RoleBasedRedirect = () => {
  const { user } = useAuthStore();

  if (user?.role === 'superAdmin') {
    return <Navigate to="/dashboard" replace />;
  } else {
    return <Navigate to="/comms" replace />;
  }
};

// Fallback 404 page
const PageNotFound = () => (
  <div className="flex flex-1 items-center justify-center text-2xl text-gray-500">
    404 - Page Not Found
  </div>
);

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRoute />,
    children: [
      {
        index: true,
        element: <RoleBasedRedirect />,
      },
      {
        path: 'dashboard',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <SuperAdminDashboard />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'comms',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <FeatureRoute feature="messages">
              <CommsPage />
            </FeatureRoute>
          </RoleBasedAccess>
        ),
      },
      {
        path: 'data',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <DataPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'apps',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <GovifyAppsPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'people',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <FeatureRoute feature="people">
              <GovifyPeoplePage />
            </FeatureRoute>
          </RoleBasedAccess>
        ),
      },

      {
        path: 'documents',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <FeatureRoute feature="documents">
              <GovifyDocumentsPage />
            </FeatureRoute>
          </RoleBasedAccess>
        ),
      },
      {
        path: 'redaction',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <FeatureRoute feature="redaction">
              <RedactionLandingPage />
            </FeatureRoute>
          </RoleBasedAccess>
        ),
      },
      {
        path: 'redaction/edit',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <FeatureRoute feature="redaction">
              <RedactionPage />
            </FeatureRoute>
          </RoleBasedAccess>
        ),
      },
      {
        path: 'chat-analytics',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <FeatureRoute feature="chat_analytics">
              <ChatAnalyticsPage />
            </FeatureRoute>
          </RoleBasedAccess>
        ),
      },
      {
        path: 'calendar',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <FeatureRoute feature="calendar">
              <GovifyCalendarPage />
            </FeatureRoute>
          </RoleBasedAccess>
        ),
      },
      {
        path: 'settings-horizontal',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <GovifySettingsPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'settings',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin', 'staff']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <GovifySettingsPageVertical />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'user-management',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'admin']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <UserManagementPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'app',
        element: (
          <RoleBasedAccess
            allowedRoles={['owner', 'superAdmin', 'admin']}
            fallback={<Navigate to="/dashboard" replace />}
          >
            <MarketPlace />
          </RoleBasedAccess>
        ),
      },
      // SuperAdmin specific routes
      {
        path: 'organizations',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <OrganizationManagementPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'organizations/create',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <CreateOrganizationPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'users',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <SuperAdminDashboard />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'users/sessions',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <SuperAdminDashboard />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'system/settings',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <SuperAdminDashboard />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'system/logs',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <SuperAdminDashboard />
          </RoleBasedAccess>
        ),
      },
      {
        path: 're-entry',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <Coordinators />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'facilities',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <FacilitiesPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'reentry/resources',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin']}
            fallback={<Navigate to="/comms" replace />}
          >
            <ResourcesPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'inmate-onboarding-form-builder',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin', 'coordinator']}
            fallback={<Navigate to="/comms" replace />}
          >
            <InmateOnboardingFormBuilder />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'organization-onboarding',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin', 'coordinator']}
            fallback={<Navigate to="/comms" replace />}
          >
            <OrganizationOnboarding />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'organization/:name',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin', 'coordinator']}
            fallback={<Navigate to="/comms" replace />}
          >
            <OrganizationDetailPage />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'organization/applications-form-builder',
        element: (
          <RoleBasedAccess
            allowedRoles={['superAdmin', 'coordinator']}
            fallback={<Navigate to="/comms" replace />}
          >
            <OrganizationApplicationsForm />
          </RoleBasedAccess>
        ),
      },
      {
        path: 'inmate-onboarding-form',
        element: (
          <RoleBasedAccess
            allowedRoles={['inmate']}
            fallback={<Navigate to="/comms" replace />}
          >
            <InmateOnboardingForm inmateId="current-user-id" />
          </RoleBasedAccess>
        ),
      },
      // Reentry Forms (org user routes — gated by feature flag)
      {
        path: 'reentry/forms',
        element: (
          <FeatureRoute feature="reentry_forms">
            <ReentryFormsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'reentry/forms/:id/edit',
        element: (
          <FeatureRoute feature="reentry_forms">
            <FormBuilderPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'reentry/analytics',
        element: (
          <FeatureRoute feature="reentry_forms">
            <ReentryAnalyticsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'reentry/devices',
        element: (
          <FeatureRoute feature="reentry_forms">
            <ReentryDevicesPage />
          </FeatureRoute>
        ),
      },
      // Grants demo
      {
        path: 'grants',
        element: (
          <FeatureRoute feature="grants">
            <RoleBasedAccess
              allowedRoles={['owner', 'admin', 'staff']}
              fallback={<Navigate to="/dashboard" replace />}
            >
              <GrantsPage page="overview" />
            </RoleBasedAccess>
          </FeatureRoute>
        ),
      },
      {
        path: 'grants/sponsorship',
        element: (
          <FeatureRoute feature="grants">
            <RoleBasedAccess
              allowedRoles={['owner', 'admin', 'staff']}
              fallback={<Navigate to="/dashboard" replace />}
            >
              <GrantsPage page="sponsorship" />
            </RoleBasedAccess>
          </FeatureRoute>
        ),
      },
      {
        path: 'grants/applicants',
        element: (
          <FeatureRoute feature="grants">
            <RoleBasedAccess
              allowedRoles={['owner', 'admin', 'staff']}
              fallback={<Navigate to="/dashboard" replace />}
            >
              <GrantsPage page="applicants" />
            </RoleBasedAccess>
          </FeatureRoute>
        ),
      },
      // OAuth callback routes for email providers
      {
        path: 'email/:provider/callback',
        element: <OAuthCallback />,
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],

    /////////////////////////////////////////////
  },
  // Kiosk route — outside authenticated layout (uses kiosk token auth)
  {
    path: '/reentry/kiosk',
    element: (
      <KioskGuard>
        <KioskForm />
      </KioskGuard>
    ),
  },
  // Legacy route — redirect tablets that still have the old URL bookmarked
  {
    path: '/reentry/kiosk/:formId',
    element: <Navigate to="/reentry/kiosk" replace />,
  },

  {
    path: '/auth',
    children: [
      {
        path: 'login',
        element: (
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        ),
      },
      {
        path: 'forgot-password',
        element: (
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        ),
      },
      {
        path: 'setup-password/:token',
        element: (
          <PublicRoute>
            <SetupPasswordPage />
          </PublicRoute>
        ),
      },
    ],
  },
  {
    path: '/privacy-policy',
    element: <PrivacyPolicy />,
  },
  {
    path: '/terms-of-service',
    element: <TermsAndConditions />,
  },
  {
    path: '*',
    element: <PageNotFound />,
  },
]);
export default router;
